/**
 * TTS Core Engine - Bộ điều khiển trung tâm Text-to-Speech
 * - Ưu tiên chạy 100% ở Frontend bằng Web Speech API Native (0 request Backend).
 * - Phân định rõ ràng: Giọng Thiết Bị (Offline 100% FE) vs Giọng Đám Mây (AI Cloud API).
 * - TUYỆT ĐỐI KHÔNG TỰ ĐỘNG GỌI API MẠNG KHI NGƯỜI DÙNG ĐANG CHỌN GIỌNG THIẾT BỊ.
 * - Hỗ trợ thanh trượt tốc độ mượt mà realtime (0.5x đến 2.5x).
 * - Quản lý Segments, Queue, Playback, Seek, Pause, Resume, Stop.
 */
import { TTS_EVENTS, TTS_STATES } from './constants/ttsEvents.js';
import { TextSegmenterService } from './services/textSegmenter.js';
import { VietnameseAudioDriver } from './drivers/vietnameseAudioDriver.js';
import { SpeechSynthesisDriver } from './drivers/speechSynthesisDriver.js';

export class TTSEngine {
  constructor() {
    this.state = TTS_STATES.IDLE;
    this.events = new Map();

    // Dữ liệu tài liệu & phân đoạn câu
    this.currentDocument = null;
    this.segments = [];
    this.currentIndex = 0;

    // Driver phát âm thanh
    this.audioDriver = new VietnameseAudioDriver();
    this.speechDriver = new SpeechSynthesisDriver();

    // MẶC ĐỊNH: Chạy 100% ở Frontend bằng Giọng Thiết Bị (0 request máy chủ)
    this.driver = this.speechDriver;
    this.currentVoice = 'native-auto';

    this.speed = 1.0;
    this.volume = 1.0;

    // Cờ kiểm soát vòng lặp đọc
    this.isPlaying = false;
    this.generationId = 0;
  }

  /**
   * Kiểm tra xem engine có đang ở chế độ 100% FE không
   */
  isNativeMode() {
    return this.driver === this.speechDriver;
  }

  on(event, callback) {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event).push(callback);
    return this;
  }

  off(event, callback) {
    if (!this.events.has(event)) return;
    const listeners = this.events.get(event).filter(cb => cb !== callback);
    this.events.set(event, listeners);
  }

  emit(event, payload) {
    if (this.events.has(event)) {
      this.events.get(event).forEach(cb => {
        try {
          cb(payload);
        } catch (e) {
          console.error(`[TTSEngine] Lỗi event listener ${event}:`, e);
        }
      });
    }
  }

  setState(newState) {
    this.state = newState;
  }

  /**
   * Lấy danh sách giọng đọc phân nhóm rõ ràng (Thiết bị Offline vs Đám mây AI)
   */
  getAvailableVoiceGroups() {
    const nativeList = [
      { id: 'native-auto', name: '💻 Giọng Thiết Bị (Tự động tốt nhất)', isDefault: true }
    ];

    if (this.speechDriver) {
      const systemVoices = this.speechDriver.getVietnameseVoices();
      systemVoices.forEach(v => {
        nativeList.push({
          id: `native-${v.name}`,
          name: `💻 ${v.name}`,
        });
      });
    }

    const cloudList = [
      { id: 'ai-female', name: '🌟 Giọng Đám Mây AI Nữ (Chuẩn & Dịu dàng)' },
      { id: 'ai-male', name: '🌟 Giọng Đám Mây AI Nam (Trầm ấm)' },
    ];

    return {
      native: nativeList,
      cloud: cloudList,
    };
  }

  getAvailableVoices() {
    const groups = this.getAvailableVoiceGroups();
    return [...groups.native, ...groups.cloud];
  }

  /**
   * Thay đổi giọng đọc ('native-auto', 'native-...', 'ai-female', 'ai-male')
   * Tự động chuyển đổi mượt mà giữa Cloud và Native Frontend mà không cần tắt widget đi bật lại
   * @param {string} voiceId 
   */
  setVoice(voiceId) {
    if (!voiceId) return;
    const oldDriver = this.driver;
    this.currentVoice = voiceId;

    if (voiceId === 'native-auto') {
      this.speechDriver.setVoiceByName('auto');
      this.driver = this.speechDriver;
      if (this.audioDriver) this.audioDriver.stop();
    } else if (voiceId.startsWith('native-')) {
      const voiceName = voiceId.replace('native-', '');
      this.speechDriver.setVoiceByName(voiceName);
      this.driver = this.speechDriver;
      if (this.audioDriver) this.audioDriver.stop();
    } else {
      // CHỈ KHI NGƯỜI DÙNG CHỦ ĐỘNG CHỌN AI CLOUD THÌ MỚI DÙNG audioDriver
      this.audioDriver.setVoice(voiceId);
      this.driver = this.audioDriver;
      if (this.speechDriver) this.speechDriver.stop();
    }

    this.driver.setSpeed(this.speed);
    this.driver.setVolume(this.volume);

    console.log('[TTSEngine] Đã chọn giọng đọc:', voiceId, '| Nguồn:', this.driver === this.speechDriver ? '100% FE (Native)' : 'Cloud API');

    // NẾU ĐANG PHÁT MÀ NGƯỜI DÙNG ĐỔI GIỌNG: Lập tức phát tiếp câu hiện tại bằng giọng mới mà không cần tắt bật lại
    if (this.isPlaying) {
      if (oldDriver && typeof oldDriver.stop === 'function') {
        oldDriver.stop();
      }
      this.generationId++;
      this.processCurrentSegment(this.generationId);
    }
  }

  loadDocument(doc) {
    if (!doc || !Array.isArray(doc.paragraphs)) {
      console.warn('[TTSEngine] Dữ liệu document không hợp lệ');
      return;
    }

    this.stop();

    this.currentDocument = { id: doc.id, title: doc.title };
    this.segments = TextSegmenterService.segmentParagraphs(doc.paragraphs);
    this.currentIndex = 0;

    this.setState(TTS_STATES.READY);
    this.emit(TTS_EVENTS.READY, {
      documentId: doc.id,
      totalSegments: this.segments.length,
    });

    console.log(`[TTSEngine] Đã nạp tài liệu "${doc.title}" với ${this.segments.length} câu đọc.`);
  }

  async play(target = null) {
    if (this.segments.length === 0) {
      console.warn('[TTSEngine] Chưa có nội dung truyện để đọc.');
      return;
    }

    if (typeof target === 'number') {
      this.currentIndex = Math.max(0, Math.min(this.segments.length - 1, target));
    } else if (typeof target === 'string') {
      const foundIdx = this.segments.findIndex(s => s.id === target);
      if (foundIdx !== -1) this.currentIndex = foundIdx;
    }

    this.isPlaying = true;
    this.setState(TTS_STATES.PLAYING);
    this.emit(TTS_EVENTS.PLAYBACK_STARTED, { currentIndex: this.currentIndex });

    this.generationId++;
    await this.processCurrentSegment(this.generationId);
  }

  /**
   * Xử lý đọc câu hiện tại và chuyển sang câu tiếp theo
   */
  async processCurrentSegment(genId) {
    if (!this.isPlaying || genId !== this.generationId) return;

    if (this.currentIndex >= this.segments.length) {
      this.isPlaying = false;
      this.setState(TTS_STATES.COMPLETED);
      this.emit(TTS_EVENTS.COMPLETED, { total: this.segments.length });
      return;
    }

    const currentSeg = this.segments[this.currentIndex];

    this.emit(TTS_EVENTS.SEGMENT_CHANGED, {
      segmentId: currentSeg.id,
      index: this.currentIndex,
      total: this.segments.length,
      text: currentSeg.text,
      paragraphIndex: currentSeg.paragraphIndex,
    });

    // CHỈ PREFETCH KHI ĐANG Ở CHẾ ĐỘ CLOUD AUDIO DRIVER (KHÔNG GỌI KHI Ở NATIVE 100% FE)
    if (this.driver === this.audioDriver) {
      const nextSeg = this.segments[this.currentIndex + 1];
      if (nextSeg && typeof this.driver.prefetch === 'function') {
        this.driver.prefetch(nextSeg.text);
      }
    }

    try {
      await this.driver.speak(
        currentSeg.text,
        () => {
          if (this.isPlaying && genId === this.generationId) {
            this.currentIndex++;
            this.processCurrentSegment(genId);
          }
        },
        (err) => {
          console.warn('[TTSEngine] Cảnh báo phát âm:', err ? err.message : err);
          // TUYỆT ĐỐI KHÔNG TỰ ĐỘNG FALLBACK GỌI API MẠNG KHI ĐANG Ở CHẾ ĐỘ NATIVE!
          if (this.isPlaying && genId === this.generationId) {
            this.currentIndex++;
            this.processCurrentSegment(genId);
          }
        }
      );
    } catch (err) {
      console.error('[TTSEngine] Lỗi trong quá trình đọc:', err);
      if (this.isPlaying && genId === this.generationId) {
        this.currentIndex++;
        this.processCurrentSegment(genId);
      }
    }
  }

  pause() {
    this.isPlaying = false;
    this.driver.pause();
    this.setState(TTS_STATES.PAUSED);
    this.emit(TTS_EVENTS.PLAYBACK_PAUSED, { currentIndex: this.currentIndex });
  }

  resume() {
    if (this.state === TTS_STATES.PAUSED) {
      // Kiem tra xem driver hien tai co thuc su dang bi paused giua mot cau khong
      // Neu nguoi dung vua doi tu Cloud sang FE (hoac nguoc lai), driver moi chua co audio bi pause
      const canDriverResume = (this.driver === this.speechDriver && this.speechDriver.synth && this.speechDriver.synth.paused) ||
                              (this.driver === this.audioDriver && this.audioDriver.audio && this.audioDriver.audio.paused && this.audioDriver.audio.currentTime > 0);

      if (canDriverResume) {
        this.isPlaying = true;
        this.setState(TTS_STATES.PLAYING);
        this.emit(TTS_EVENTS.PLAYBACK_RESUMED, { currentIndex: this.currentIndex });
        this.driver.resume();
      } else {
        // Tu dong phat tiep cau hien tai bang driver moi, tranh bi treo im lang
        this.play(this.currentIndex);
      }
    } else {
      this.play(this.currentIndex);
    }
  }

  stop() {
    this.generationId++;
    this.isPlaying = false;
    this.driver.stop();
    this.setState(TTS_STATES.STOPPED);
    this.emit(TTS_EVENTS.STOPPED);
  }

  seekToParagraph(pIdx) {
    if (!this.segments || this.segments.length === 0) return;
    const foundIdx = this.segments.findIndex(s => s.paragraphIndex === pIdx);
    if (foundIdx !== -1) {
      this.seek(foundIdx);
    } else {
      this.seek(pIdx);
    }
  }

  seek(target) {
    this.stop();
    this.play(target);
  }

  next() {
    if (this.currentIndex < this.segments.length - 1) {
      this.seek(this.currentIndex + 1);
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.seek(this.currentIndex - 1);
    }
  }

  setSpeed(speed) {
    this.speed = Math.max(0.5, Math.min(2.5, speed));
    this.driver.setSpeed(this.speed);
  }

  setVolume(volume) {
    this.volume = volume;
    this.driver.setVolume(volume);
  }
}
